import React from 'react';
import { Box, Grid, Button, Divider, CircularProgress, Typography, TextField, FormControl, InputLabel, Select, MenuItem, Paper } from '@mui/material';
import { getRequest, postRequest } from 'Includes/api/apicall';
import { GET_URL, POST_URL } from 'Includes/urls';
import Swal from 'sweetalert2';
import { AddCircleOutlineOutlined, VisibilityOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom'; 
function CompanyBillingConfigAdd(props) {
  const agreementOrderFlow = props.location?.state?.agreementOrderFlow || null;
  const targetCompanyId = agreementOrderFlow?.companyId || null;
  const [loading, set_loading] = React.useState(false);
  const [submitDisable, set_submitDisable] = React.useState(false);
  const navigate = useNavigate();
  const [companies, setCompanies] = React.useState([]);
  const [years, setYears] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [year, setYear] = React.useState('');
  const [items, setItems] = React.useState([{ company: '', student_strength: '', coordinator: '' }]);
  const [yearError, setYearError] = React.useState('');
  const [itemErrors, setItemErrors] = React.useState([]);

  React.useEffect(() => {
    getAcademicYears();
    getUsers();
  }, []);

  React.useEffect(() => {
    if (!targetCompanyId) return;
    setItems((prev) => {
      const next = [...prev];
      const first = next[0] || { company: '', student_strength: '', coordinator: '' };
      next[0] = { ...first, company: Number(targetCompanyId) };
      return next;
    });
  }, [targetCompanyId]);

  const getCompanies = (selectedYearId) => {
    if (!selectedYearId) {
      setCompanies([]);
      return;
    }

    // Fetch CompanySecondaryDetails to get list of companies that have billing details
    const secondaryDetailsUrl = GET_URL.companysecondarydetails.api;
    getRequest(secondaryDetailsUrl, {}, props).then((secondaryResponse) => {
      if (secondaryResponse && secondaryResponse.status === 200) {
        const secondaryDetails = secondaryResponse.data?.data || secondaryResponse.data || [];
        
        // Extract unique companies from secondary details
        // CompanySecondaryDetails includes: company (ID) and company_name
        const companiesMap = new Map();
        secondaryDetails.forEach(detail => {
          if (detail.company) {
            // Use company ID as key to ensure uniqueness
            if (!companiesMap.has(detail.company)) {
              companiesMap.set(detail.company, {
                id: detail.company,
                name: detail.company_name || '-'
              });
            }
          }
        });
        
        // Fetch existing CompanyBillingConfig for the selected year to exclude already mapped companies
        const billingConfigUrl = GET_URL.company_billing_config.api;
        getRequest(billingConfigUrl, { year: selectedYearId }, props).then((configResponse) => {
          if (configResponse && configResponse.status === 200) {
            const existingConfigs = configResponse.data?.data || configResponse.data || [];
            // Extract company IDs that are already mapped to this year
            const mappedCompanyIds = new Set();
            existingConfigs.forEach(config => {
              if (config.company) {
                mappedCompanyIds.add(config.company);
              }
            });
            
            // Filter companies to exclude already mapped ones
            const filteredCompanies = Array.from(companiesMap.values()).filter(company => 
              !mappedCompanyIds.has(company.id)
            );
            setCompanies(filteredCompanies);
          }
        }).catch(() => {
          // If billing config fetch fails, show all companies from secondary details
          const allCompanies = Array.from(companiesMap.values());
          setCompanies(allCompanies);
        });
      }
    }).catch(() => {
      // If secondary details fetch fails, set empty array
      setCompanies([]);
    });
  };

  const getAcademicYears = () => {
    const url = GET_URL.academicyear.api;
    getRequest(url, {}, props).then((response) => {
      if (response && response.status === 200) {
        setYears(response.data.data || response.data || []);
      }
    });
  };

  const getUsers = () => {
    const url = GET_URL.user.api;
    getRequest(url, { groups: 4 }, props).then((response) => {
      if (response && response.status === 200) {
        setUsers(response.data.data || response.data || []);
      }
    });
  };

  const handleYearChange = (value) => {
    setYear(value);
    setYearError('');
    // Reset items when year changes
    setItems([{ company: '', student_strength: '', coordinator: '' }]);
    setItemErrors([]);
    // Fetch companies for the selected year (excluding already mapped ones)
    getCompanies(value);
  };

  const handleItemChange = (index, name, value) => {
    const next = items.slice();
    next[index] = { ...next[index], [name]: value };
    setItems(next);
    const nextErr = itemErrors.slice();
    nextErr[index] = { ...(nextErr[index] || {}), [name]: '' };
    setItemErrors(nextErr);
  };

  const addItem = () => {
    setItems(prev => [...prev, { company: '', student_strength: '', coordinator: '' }]);
    setItemErrors(prev => [...prev, {}]);
  };

  const validate = () => {
    let valid = true;
    if (!year) {
      setYearError('Academic Year is required');
      valid = false;
    }
    const errs = [];
    items.forEach((row, idx) => {
      const rowErr = {};
      if (!row.company) {
        rowErr.company = 'Company is required';
        valid = false;
      }
      // Validate student_strength
      if (!row.student_strength || row.student_strength === '' || row.student_strength === null || row.student_strength === undefined) {
        rowErr.student_strength = 'Student Strength is required';
        valid = false;
      } else {
        const ss = parseFloat(row.student_strength);
        if (isNaN(ss)) {
          rowErr.student_strength = 'Student Strength must be a valid number';
          valid = false;
        } else if (ss <= 0) {
          rowErr.student_strength = 'Student Strength must be greater than 0';
          valid = false;
        } else if (ss > 9999) {
          rowErr.student_strength = 'Student Strength must not exceed 9999';
          valid = false;
        } else if (!Number.isInteger(ss)) {
          rowErr.student_strength = 'Student Strength must be a whole number';
          valid = false;
        }
      }
      if (!row.coordinator || row.coordinator === '') {
        rowErr.coordinator = 'Coordinator is required';
        valid = false;
      }
      errs[idx] = rowErr;
    });
    setItemErrors(errs);
    return valid;
  };

  const submit = async () => {
    if (!validate()) return;
    
    // Additional validation before submit - ensure all student_strength values are valid
    for (let i = 0; i < items.length; i++) {
      const row = items[i];
      const ss = parseFloat(row.student_strength);
      if (!row.student_strength || isNaN(ss) || ss <= 0 || ss > 9999 || !Number.isInteger(ss)) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: `Invalid Student Strength for row ${i + 1}. Please enter a valid number between 1 and 9999.`,
          timer: 3000
        });
        return;
      }
    }
    
    set_submitDisable(true);
    const url = POST_URL.company_billing_config.api;
    const payload = items.map(row => {
      return {
        company: row.company,
        year: year,
        student_strength: parseInt(row.student_strength, 10),
        coordinator: Number(row.coordinator),
      }
    });
    const response = await postRequest(url, payload, {})
    set_submitDisable(false);
    if (response?.status === 200) {
      Swal.fire({
        position: 'top-end',
        type: 'success',
        title: 'Success',
        text: 'Company Billing Configs created',
        showConfirmButton: false,
        timer: 1500
      });
      if (targetCompanyId) {
        const created = response?.data?.data;
        const createdList = Array.isArray(created) ? created : (created ? [created] : []);
        const matched = createdList.find((item) => Number(item?.company) === Number(targetCompanyId));
        const configId = matched?.id;
        if (configId) {
          navigate(`/billing/company-setup?config_id=${configId}`);
          return;
        }
        getRequest(
          GET_URL.company_billing_config.api,
          { company: targetCompanyId, year, limit: 1, pageno: 1 },
          props
        ).then((res) => {
          const cfg = res?.data?.data?.[0];
          if (cfg?.id) {
            navigate(`/billing/company-setup?config_id=${cfg.id}`);
            return;
          }
          navigate('/billing/company-billing-config/list');
        }).catch(() => {
          navigate('/billing/company-billing-config/list');
        });
        return;
      }
      navigate('/billing/company-billing-config/list');
    }
  };

  const resetForm = () => {
    setYear('');
    setItems([{ company: '', student_strength: '', coordinator: '' }]);
    setYearError('');
    setItemErrors([]);
    setCompanies([]);
  };

  return (
    <>
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}
      {!loading && (
        <Box sx={{ p: { xs: 2, sm: 3 }, width: '100%', bgcolor: 'background.default' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#0a0a0a' }}>
                Create company billing config
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                Map companies to an academic year with student strength and coordinator.
              </Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap" alignSelf={{ xs: 'flex-start', sm: 'center' }}>
              <Button
                variant="contained"
                startIcon={<VisibilityOutlined />}
                onClick={() => {
                  if (navigate.length > 1) {
                    navigate(-1);
                  } else {
                    navigate('/billing/company-billing-config/list');
                  }
                }}
                sx={{ textTransform: 'none', borderRadius: 2, px: 2.5, py: 1 }}
              >
                View configurations
              </Button>
            </Box>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                borderColor: 'divider',
                bgcolor: 'grey.50',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: '#0a0a0a', fontWeight: 700, mb: 1 }}>
                Basic configuration
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5 }}>
                Choose the academic year to load companies that already have billing details.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth variant="outlined" size="small" error={!!yearError} required>
                    <InputLabel id="year-label">Academic Year</InputLabel>
                    <Select
                      labelId="year-label"
                      label="Academic Year"
                      value={year}
                      onChange={(e) => handleYearChange(e.target.value)}
                      error={!!yearError}
                    >
                      {years.map(y => (
                        <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {yearError && (
                    <Typography variant="caption" color="error">{yearError}</Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>

          {year && (
            <>
              <Typography
                component="h2"
                variant="subtitle1"
                sx={{
                  color: '#0a0a0a',
                  fontWeight: 700,
                  mb: 1.5,
                  mt: 0.5,
                  pb: 0.75,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                Company billing rows
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5 }}>
                Add one or more company rows for this year.
              </Typography>
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddCircleOutlineOutlined />}
                  onClick={addItem}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Add row
                </Button>
              </Box>

                <Grid container spacing={2}>
                {items.map((row, idx) => (
                  <React.Fragment key={idx}>
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Row {idx + 1}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth variant="outlined" size="small" error={!!(itemErrors[idx]?.company)} required>
                        <InputLabel id={`company-label-${idx}`}>Company</InputLabel>
                        <Select
                          labelId={`company-label-${idx}`}
                          label="Company"
                          value={row.company}
                          onChange={(e) => handleItemChange(idx, 'company', e.target.value)}
                          error={!!(itemErrors[idx]?.company)}
                        >
                          {companies.map(c => (
                            <MenuItem key={c.id} value={c.id}>{c.name || c.company_name || '-'}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {(itemErrors[idx]?.company) && (
                        <Typography variant="caption" color="error">{itemErrors[idx]?.company}</Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Student Strength"
                        type="number"
                        variant="outlined"
                        fullWidth
                        size="small"
                        required
                        value={row.student_strength}
                        onChange={(e) => handleItemChange(idx, 'student_strength', e.target.value)}
                        inputProps={{ min: 1, max: 9999, step: 1 }}
                        error={!!(itemErrors[idx]?.student_strength)}
                        helperText={itemErrors[idx]?.student_strength || ''}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth variant="outlined" size="small" error={!!(itemErrors[idx]?.coordinator)} required>
                        <InputLabel id={`coordinator-label-${idx}`}>Coordinator</InputLabel>
                        <Select
                          labelId={`coordinator-label-${idx}`}
                          label="Coordinator"
                          value={row.coordinator}
                          onChange={(e) => handleItemChange(idx, 'coordinator', e.target.value)}
                        >
                          {users.map(u => (
                            <MenuItem key={u.id} value={u.id}>
                              {u.username || u.email || u.id}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {(itemErrors[idx]?.coordinator) && (
                        <Typography variant="caption" color="error">{itemErrors[idx]?.coordinator}</Typography>
                      )}
                    </Grid>
                  </React.Fragment>
                ))}
                </Grid>

              <Box mt={3} mb={3}><Divider /></Box>

              <Box display='flex' justifyContent='flex-end' gap={2} className='mv-20'>
                <Button variant='outlined' onClick={resetForm} sx={{ px: 3 }}>Reset</Button>
                <Button
                  variant="contained"
                  color="primary"
                  className="submit"
                  disabled={submitDisable}
                  onClick={submit}
                  sx={{ px: 4, fontWeight: 600 }}
                >
                  {submitDisable ? 'Submitting...' : 'Submit'}
                </Button>
              </Box>
            </>
          )}
          </Paper>
        </Box>
      )}
    </>
  );
}

export default CompanyBillingConfigAdd;
